// LifePolicyRegister.jsx
import React, { useState, useEffect, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";
import { Mail } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { RiFileExcel2Line } from "react-icons/ri";
import { useNavigate } from 'react-router-dom';
import { useGetGroupsQuery, useLazyPreviewPolicyReportQuery, useGetPolicyReportQuery, useExportPolicyReportMutation, useGetExportStatusQuery, useDownloadExportQuery } from '../../../../../services/redux/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { FaFilePdf, FaRegFilePdf } from "react-icons/fa";
import { API_BASE_URL } from '../../../../../services/config';

/* Tailwind tokens (ensure you have brandRed in tailwind.config.js) */
const fieldBase =
  "rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-200 ";
const labelBase = " font-medium text-gray-700";
const sectionHeader = "bg-gray-100 p-3 rounded-md  font-semibold text-gray-600";
const btnPrimary =
  "bg-brandRed hover:bg-red-600 text-white font-medium px-6 py-2 rounded-md shadow-md transition";

/* outside click hook for dropdown */
function useOutsideClick(ref, handler) {
  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) handler();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [ref, handler]);
}

/**
 * Normalize mutation/query hook shapes:
 * - RTK-toolkit codegen often returns: [trigger, result]
 * - Some setups return an object: { trigger, data, isLoading, error, ... }
 * This helper returns { trigger, result } consistently.
 */
function normalizeHook(hook) {
  // default
  let trigger;
  let result = {};

  if (Array.isArray(hook)) {
    // [trigger, result]
    trigger = hook[0];
    result = hook[1] ?? {};
  } else if (hook && typeof hook === 'object') {
    // object shape: { trigger/mutate, data, isLoading, error, status }
    trigger = hook.trigger ?? hook.mutate ?? hook;
    result = {
      data: hook.data,
      isLoading: hook.isLoading ?? hook.isFetching ?? hook.status === 'pending',
      error: hook.error,
      status: hook.status,
    };
  } else {
    trigger = undefined;
    result = {};
  }

  return { trigger, result };
}

/* Compact multi-select (fixed height to align with selects) */
function MultiSelectInline({ options = [], value = [], onChange, placeholder = "Select groups..." }) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const rootRef = useRef(null);
  useOutsideClick(rootRef, () => setOpen(false));

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${fieldBase} min-h-[40px] py-2 w-full flex items-center gap-2 justify-between bg-white`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 flex-1 flex-wrap max-h-24 overflow-y-auto">
          {value.length === 0 ? (
            <span className="text-gray-400  truncate">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1 flex-wrap">
              {value.map((id) => {
                const o = options.find((x) => x.id === id);
                return (
                  <span
                    key={id}
                    className="text-xs bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 whitespace-nowrap"
                  >
                    {o?.label ?? id}
                  </span>
                );
              })}
            </div>
          )}
        </div>
 
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-500">{value.length > 0 ? `${value.length} selected` : ""}</span>
          <FiChevronDown className="text-gray-500" />
        </div>
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 shadow-lg rounded-md overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-auto p-2">
            {filteredOptions.length === 0 ? (
              <div className="text-gray-400 text-center py-2">No groups found</div>
            ) : (
              filteredOptions.map((o) => (
                <label key={o.id} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer">
                  <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4" />
                  <span className=" text-gray-700">{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LifePolicyRegister() {
  const [showPdfModal, setShowPdfModal] = React.useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = React.useState(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [triggerPreview, previewResult] = useLazyPreviewPolicyReportQuery();
  const [excelPayload, setExcelPayload] = React.useState(null);
  const [jobId, setJobId] = React.useState(null);
  const [jobStatusText, setJobStatusText] = React.useState('');
  const [jobCreating, setJobCreating] = React.useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [policyStatus, setPolicyStatus] = useState("");
  const [groupsSelected, setGroupsSelected] = useState([]);
  const [sortBy, setSortBy] = useState("group");
  const [reportFields, setReportFields] = useState({ address: false, mobile: false, email: false });
  const navigate = useNavigate();

  // load groups from RTK Query
  const { data: groupsResult, isFetching: groupsLoading, error: groupsError } = useGetGroupsQuery({ pageNumber: 1, pageSize: 1000 });
  
  // RTK Query for Excel export data
  const { data: excelData, isLoading: excelLoading, isError: excelError } = useGetPolicyReportQuery(
    1, 
    10000, 
    excelPayload || {}, 
    { skip: !excelPayload }
  );

  // Export job hooks
  const [createExportJob, createJobResult] = useExportPolicyReportMutation();
  
  const { data: jobStatusData } = useGetExportStatusQuery(jobId, { pollingInterval: 2000, skip: !jobId });
  
  const { refetch: triggerDownload } = useDownloadExportQuery(jobId, { skip: true });


  const groupsOptions = React.useMemo(() => {
    const rows = groupsResult?.data;
    if (!rows || !Array.isArray(rows)) return [];
    return rows.map((g) => {
      const code = g.GroupCode ?? g.groupCode ?? '';
      const name = g.GroupName ?? g.GroupName ?? g.DisplayName ?? g.Name ?? '';
      const labelParts = [];
      if (code) labelParts.push(String(code));
      if (name) labelParts.push(String(name));
      const label = labelParts.length ? labelParts.join(' - ') : String(g.GroupID ?? g.id ?? g.GroupId ?? g.groupId ?? '');
      return {
        id: g.GroupID ?? g.id ?? g.GroupId ?? g.groupId ?? String(code || name || ''),
        label,
      };
    });
  }, [groupsResult]);

  const buildPayload = () => {
    const payload = {
      sortBy: mapSortForBackend(sortBy),
      includeAddress: reportFields.address ? '1' : '0',
      includeMobile: reportFields.mobile ? '1' : '0',
      includeEmail: reportFields.email ? '1' : '0',
      filterByDate: 'comm',
      includeLapsed: '0',
      reportTitle: 'Policy Register',
      sortingOrder: mapSortForBackend(sortBy)
    };
    
    // Match exactly how handleGenerate builds the payload
    if (fromDate) payload.fromDate = fromDate;
    if (toDate) payload.toDate = toDate;
    if (paymentMode) payload.paymentMode = paymentMode;
    if (policyStatus) payload.policyStatus = policyStatus;
    if (groupsSelected && groupsSelected.length) payload.groupIds = groupsSelected.join(",");
    
    console.log('Building payload:', payload);
    return payload;
  };

  const handlePdfPreview = async () => {
    try {
      setIsGenerating(true);
      const payload = buildPayload();
      console.log('PDF Preview Payload:', payload);
      const result = await triggerPreview(payload).unwrap();
      console.log('PDF Preview Result:', result);
      if (result?.pdf) {
        const byteCharacters = atob(result.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        setPdfObjectUrl(URL.createObjectURL(new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' })));
        setShowPdfModal(true);
      } else {
        console.error('No PDF data in response');
        alert('No PDF data received from server.');
      }
    } catch (error) {
      console.error('PDF preview error details:', error);
      console.error('Error status:', error.status);
      console.error('Error data:', error.data);
      if (error.status === 404) {
        alert('No data found for the selected filters. Please adjust your filter criteria and try again.');
      } else {
        const errorMessage = error?.data?.message || error?.message || 'Failed to generate PDF.';
        alert(`Failed to generate PDF: ${errorMessage}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Effect to handle Excel export when data is ready
  React.useEffect(() => {
    if (!excelPayload || excelLoading || excelError) return;
    if (!excelData) return;
    
    const rows = Array.isArray(excelData) ? excelData : (excelData?.data ?? []);
    
    if (!rows.length) {
      alert('No data available for the selected filters.');
      setIsGenerating(false);
      setExcelPayload(null);
      return;
    }
    
    const generateExcel = async () => {
      try {
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Policy Register');
        
        const columns = [
          { header: 'Group Code', key: 'GroupCode', width: 20 }, 
          { header: 'Area', key: 'Area', width: 25 }, 
          { header: 'Life Assured', key: 'LifeAssured', width: 25 }, 
          { header: 'Policy No', key: 'PolicyNo', width: 20 }, 
          { header: 'Product', key: 'Product', width: 25 }, 
          { header: 'Term/PPT', key: 'TermPPT', width: 12 }, 
          { header: 'Commencement Date', key: 'CommencementDate', width: 18 }, 
          { header: 'Sum Assured', key: 'SumAssured', width: 15 }, 
          { header: 'Payment Mode', key: 'PaymentMode', width: 15 }, 
          { header: 'Net Premium', key: 'NetPremium', width: 15 }, 
          { header: 'Agency', key: 'Agency', width: 20 }
        ];
        
        if (reportFields.address) columns.push({ header: 'Address', key: 'Address', width: 30 });
        if (reportFields.mobile) columns.push({ header: 'Mobile', key: 'Mobile', width: 15 });
        if (reportFields.email) columns.push({ header: 'Email', key: 'Email', width: 25 });
        
        ws.columns = columns;
        ws.getRow(1).font = { bold: true, size: 12 };
        ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
        
        // Map rows with Area from nested raw object
        rows.forEach(row => {
          const areaValue = (row.raw && row.raw.AreaName) ?? row.AreaName ?? row.Area ?? '';
          ws.addRow({ 
            GroupCode: row.GroupCode || '', 
            Area: areaValue, 
            LifeAssured: row.LifeAssured || '', 
            PolicyNo: row.PolicyNo || '', 
            Product: row.Product || '', 
            TermPPT: row.TermPPT || '', 
            CommencementDate: row.CommDate || row.CommencementDate || '', 
            SumAssured: row.SumAssured || '', 
            PaymentMode: row.PaymentMode || '', 
            NetPremium: row.NetPremium || '', 
            Agency: row.Agency || '', 
            Address: row.Address || '', 
            Mobile: row.Mobile || '', 
            Email: row.Email || '' 
          });
        });
        
        saveAs(new Blob([await wb.xlsx.writeBuffer()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Policy_Register_${new Date().toISOString().slice(0, 10)}.xlsx`);
      } catch (error) {
        console.error('Excel generation error:', error);
        alert('Failed to generate Excel file.');
      } finally {
        setIsGenerating(false);
        setExcelPayload(null);
      }
    };
    
    generateExcel();
  }, [excelData, excelPayload, excelLoading, excelError, reportFields]);

  const handleExcelExport = () => {
    setIsGenerating(true);
    const payload = buildPayload();
    setExcelPayload(payload);
  };

  React.useEffect(() => () => { if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl); }, [pdfObjectUrl]);

  // Monitor job status (matches PolicyReport.js)
  useEffect(() => {
    if (jobStatusData?.job) {
      const j = jobStatusData.job;
      setJobStatusText(j.status + (j.error ? ` — ${j.error}` : ''));
      if (j.status === 'done' && j.filePath) {
        // download
        downloadGeneratedFile(jobId).catch((e) => console.error('download error', e));
      } else if (j.status === 'failed') {
        setJobCreating(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatusData]);

  // Download generated file (matches PolicyReport.js)
  const downloadGeneratedFile = async (id) => {
    try {
      if (triggerDownload) {
        const dlRes = await triggerDownload();
        const blob = dlRes?.data;
        if (blob instanceof Blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `policy_register_${id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } else if (dlRes?.url) {
          window.location.href = dlRes.url;
        } else {
          window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
        }
      } else {
        window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
      }
    } catch (err) {
      console.error('Error downloading file', err);
      window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
    }
  };

  // Create export job and download (matches PolicyReport.js)
  const handleCreateExportJobAndDownload = async () => {
    setJobCreating(true);
    setJobStatusText('Creating export job...');
    try {
      // If we already have a PDF preview object URL, download it directly to avoid creating a job
      if (pdfObjectUrl) {
        const filename = `policy_register_preview_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
        const a = document.createElement('a');
        a.href = pdfObjectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setJobCreating(false);
        setJobStatusText('Downloaded preview PDF');
        return;
      }
      if (typeof createExportJob !== 'function') throw new Error('Create job hook not available');
      // Build export params
      const exportParams = buildPayload();
      exportParams.reportTitle = 'Policy Register';
      exportParams.reportType = 'policy-register';
      exportParams.format = 'pdf';
      const res = await createExportJob(exportParams).unwrap();
      const payload = res?.data ?? res;
      const newJobId = payload?.jobId ?? payload?.job?.id ?? payload?.id;
      if (!newJobId) throw new Error('No jobId returned from create export job');
      setJobId(newJobId);
      setJobStatusText(`Job created: ${newJobId}. Waiting for file...`);
      // polling will handle the rest
    } catch (err) {
      console.error('Export job error', err);
      alert('Export job failed: ' + (err?.message ?? err));
      setJobCreating(false);
    }
  };

  const toggleReportField = (k) => setReportFields((s) => ({ ...s, [k]: !s[k] }));

  const mapSortForBackend = (localSort) => {
    // map UI values -> backend expected sortBy values
    switch (localSort) {
      case 'group': return 'groupwise';
      case 'member': return 'memberwise';
      case 'area': return 'areawise';
      case 'commencement': return 'commencementdate';
      default: return 'groupwise';
    }
  };

  const handleGenerate = (e) => {
    e.preventDefault();
    const payload = {
      pageNumber: 1,
      pageSize: 10000,
      fromDate: fromDate || null,
      toDate: toDate || null,
      paymentMode: paymentMode || null,
      policyStatus: policyStatus || null,
      groupIds: groupsSelected && groupsSelected.length ? groupsSelected.join(',') : null,
      sortBy: mapSortForBackend(sortBy),
      includeAddress: reportFields.address,
      includeMobile: reportFields.mobile,
      includeEmail: reportFields.email,
    };
    // navigate to PolicyReport and pass the payload (PolicyReport will handle preview/export)
    navigate('/policy-report', { state: { payload } });
  };

  return (
    <div className="w-full p-4">
      <div style={{ boxShadow: "0 0 8px #ccc" }} className="w-full rounded bg-white">
        <div className="p-2 border-b border-brandRed">
          <div className="flex justify-between items-center">
            <h5 className="text-brandRed font-bold m-3">Policy Register</h5>
            <div className="flex gap-3 items-center">
              <Mail size={38} className="text-brandRed cursor-not-allowed opacity-50" />
              <RiFileExcel2Line size={38} className="text-green-700 cursor-pointer hover:scale-110 transition" onClick={handleExcelExport} title="Download Excel" />
              <FaFilePdf size={37} className="text-red-600 cursor-pointer hover:scale-110 transition" onClick={handlePdfPreview} title="Preview PDF" />
            </div>
          </div>
        </div>

        <form onSubmit={handleGenerate} className="w-full">
          <div className="p-6">
            <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2 mb-4">Filter Selection</h6>

            {/* ===== LABEL ROW (md+) - labels aligned on a single baseline ===== */}
            <div className="hidden md:grid grid-cols-12 gap-3 items-center mb-2">
              <div className="col-span-12 md:col-span-6 flex justify-center">
                <div className=" font-semibold text-gray-700">Commencement Date Period</div>
              </div>

              <div className="col-span-12 md:col-span-3">
                <div className=" font-semibold text-gray-700">Payment Mode</div>
              </div>

              <div className="col-span-12 md:col-span-3">
                <div className=" font-semibold text-gray-700">Policy Status</div>
              </div>
            </div>

            {/* ===== INPUT ROW - everything vertically centered for md+ ===== */}
            <div className="grid grid-cols-12 gap-3 items-center">
              {/* Date column (on mobile we show the global label first) */}
              <div className="col-span-12 md:col-span-6">
                {/* MOBILE global label BEFORE inputs (visible only on small) */}
                <div className="md:hidden  font-semibold text-gray-700 mb-2">
                  Commencement Date Period
                </div>

                {/* two date inputs stacked on mobile, side-by-side on md */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ">
                  {/* From */}
                  <div className="flex flex-col p-2">
                    {/* mobile field label (visible on small) */}
                    <label className="md:hidden  text-gray-700 mb-1">From</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className={`${fieldBase} h-10 w-full`}
                      aria-label="From date"
                    />
                  </div>

                  {/* To */}
                  <div className="flex flex-col p-2">
                    <label className="md:hidden  text-gray-700 mb-1">To</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className={`${fieldBase} h-10 w-full`}
                      aria-label="To date"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Mode - mobile label visible here */}
              <div className="col-span-12 md:col-span-3 p-2">
                <label className="md:hidden  text-gray-700 mb-1">Payment Mode</label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className={`${fieldBase} h-10 w-full`}
                >
                  <option value="">Select</option>
                  <option value="Yearly">Yearly</option>
              <option value="Half-Yearly">Half-Yearly</option>
                    <option value="Quarterly">Quarterly</option>
                  <option value="Monthly">Monthly</option>
                
                </select>
              </div>

              {/* Policy Status - mobile label visible here */}
              <div className="col-span-12 md:col-span-3">
                <label className="md:hidden  text-gray-700 mb-1">Policy Status</label>
                <select
                  value={policyStatus}
                  onChange={(e) => setPolicyStatus(e.target.value)}
                  className={`${fieldBase} h-10 w-full`}
                >
                  <option value="">Select</option>
                  <option value="Reduced Paid-up">Reduced Paid-up</option>
                    <option value="Fully Paid-up">Fully Paid-up</option>
                  <option value="Lapsed">Lapsed</option>
                  <option value="Surrendered">Surrendered</option>
                  <option value="In Force">In Force</option>
                </select>
              </div>
            </div>
            {/* ===== end combined block ===== */}

            {/* GROUPS row */}
            <div className="mt-6 grid grid-cols-12 gap-3 items-center p-2">
              <div className="col-span-12 lg:col-span-2">
                <div className={`${labelBase} mb-0.5`}>Select Groups</div>
              </div>

              <div className="col-span-12 lg:col-span-6 ">
                <MultiSelectInline
                  options={groupsOptions}
                  value={groupsSelected}
                  onChange={setGroupsSelected}
                  placeholder={groupsLoading ? 'Loading groups...' : 'Select groups...'}
                />
              </div>

              <div className="col-span-12 lg:col-span-4 flex items-center lg:justify-start justify-end">
                {groupsSelected.length > 0 && (
                  <div className="hidden lg:block  text-blue-600 hover:underline ml-3">
                    {groupsSelected.length} Groups Selected
                  </div>
                )}
              </div>

              {groupsSelected.length > 0 && (
                <div className="col-span-12 lg:hidden  text-center mt-2 text-blue-600 hover:underline">
                  Groups Selected: {groupsSelected.length}
                </div>
              )}
            </div>

            {/* Sorting order */}
            <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2 ">Sorting Order</h6>
            <div className="flex flex-wrap items-center gap-6  p-3">
              <label className="flex items-center gap-2">
                <input type="radio" name="sort" checked={sortBy === "group"} onChange={() => setSortBy("group")} className="w-4 h-4" />
                <span className="ml-1  text-gray-700">Group wise</span>
              </label>

              <label className="flex items-center gap-2">
                <input type="radio" name="sort" checked={sortBy === "member"} onChange={() => setSortBy("member")} className="w-4 h-4" />
                <span className="ml-1  text-gray-700">Member wise</span>
              </label>

              <label className="flex items-center gap-2">
                <input type="radio" name="sort" checked={sortBy === "area"} onChange={() => setSortBy("area")} className="w-4 h-4" />
                <span className="ml-1  text-gray-700">Area wise</span>
              </label>

              <label className="flex items-center gap-2">
                <input type="radio" name="sort" checked={sortBy === "commencement"} onChange={() => setSortBy("commencement")} className="w-4 h-4" />
                <span className="ml-1  text-gray-700">Commencement Date wise</span>
              </label>
            </div>

            {/* Add in Report */}
            <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2">Add in Report</h6>
            <div className="flex gap-6 items-center py-4 flex-wrap p-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={reportFields.address} onChange={() => toggleReportField("address")} className="w-4 h-4" />
                <span className="ml-1  text-gray-700">Address</span>
              </label>

              <label className="flex items-center gap-2">
                <input type="checkbox" checked={reportFields.mobile} onChange={() => toggleReportField("mobile")} className="w-4 h-4" />
                <span className="ml-1  text-gray-700">Mobile</span>
              </label>

              <label className="flex items-center gap-2">
                <input type="checkbox" checked={reportFields.email} onChange={() => toggleReportField("email")} className="w-4 h-4" />
                <span className="ml-1  text-gray-700">Email</span>
              </label>
            </div>

            <div className="flex justify-center mt-8">
              <button type="submit" className={btnPrimary}>
                Generate
              </button>
            </div>

            <div className="mt-6 text-center  text-gray-500">Life Insurance Policy Register Report..</div>
          </div>
        </form>
      </div>
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6 bg-black bg-opacity-40">
          <div className="bg-white rounded-md w-full max-w-6xl h-[80vh] overflow-hidden shadow-lg">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-3">
                <FaRegFilePdf className="text-red-600" />
                <div>
                  <div className="font-semibold">PDF Preview</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="text-sm text-gray-600 mr-4">{jobStatusText}</div>
                <button className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700" onClick={handleCreateExportJobAndDownload} disabled={jobCreating}>
                  {jobCreating ? 'Preparing...' : 'Download'}
                </button>
                <button
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                  onClick={() => {
                    setShowPdfModal(false);
                    if (pdfObjectUrl) {
                      URL.revokeObjectURL(pdfObjectUrl);
                      setPdfObjectUrl(null);
                    }
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="h-full">
              {previewResult?.isLoading || previewResult?.isFetching ? (
                <div className="flex items-center justify-center h-full">Generating preview...</div>
              ) : (
                <iframe title="PDF Preview" src={pdfObjectUrl} width="100%" height="100%" style={{ border: 'none' }} />
              )}
            </div>
          </div>
        </div>
      )}
      {isGenerating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl"><div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brandRed mx-auto"></div><p className="mt-4 text-gray-700">Generating report...</p></div>
        </div>
      )}
    </div>
  );
}
